/**
 * React Query hooks for documentation API endpoints
 * Following CLAUDE.md guidelines: Always use React Query for REST API calls
 */
import { useQuery } from "@tanstack/react-query";
import axiosClient from "../utils/fetchAxiosClient";

// ============================================================================
// Request/Response Types
// ============================================================================

export interface ConfluencePageInfo {
  pageId: string;
  title: string;
}

export interface ConfluencePageContent {
  pageId: string;
  title: string;
  htmlContent: string;
}

export interface DocumentationResponse {
  pages: ConfluencePageInfo[];
  content?: ConfluencePageContent;
}

/**
 * Supported documentation types for domains, SSO providers, and workday
 */
export type DocumentationType =
  | "workday"
  | "google"
  | "office365"
  | "okta"
  | "azure"
  | "ping"
  | "idaptive";

// Query keys for cache management
export const documentationKeys = {
  all: ["documentation"] as const,
  byType: (docType: string, pageId?: string) =>
    [...documentationKeys.all, docType, pageId || "default"] as const,
  pageContent: (pageId: string) =>
    [...documentationKeys.all, "page", pageId] as const
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to get documentation for a specific type (domain or SSO provider)
 * Returns list of available pages and the content of the first or specified page
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useDocumentation('google');
 * if (data?.content) {
 *   return <div dangerouslySetInnerHTML={{ __html: data.content.htmlContent }} />;
 * }
 * ```
 */
export function useDocumentation(
  docType: DocumentationType | null | undefined,
  pageId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: documentationKeys.byType(docType || "workday", pageId),
    queryFn: async (): Promise<DocumentationResponse> => {
      const params = pageId ? `?pageId=${pageId}` : "";
      const response = await axiosClient.get<DocumentationResponse>(
        `/onboarding-documentation/${docType}${params}`
      );
      return response.data;
    },
    enabled: !!docType && options?.enabled !== false,
    // Documentation content rarely changes, cache for longer
    staleTime: 30 * 60 * 1000, // 30 minutes
    // Keep in cache even when component unmounts
    cacheTime: 60 * 60 * 1000, // 1 hour (v4 uses cacheTime, not gcTime)
    retry: 1
  });
}

/**
 * Hook to get content for a specific Confluence page
 *
 * @example
 * ```tsx
 * const { data, isLoading } = usePageContent(selectedPageId);
 * ```
 */
export function usePageContent(
  pageId: string | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: documentationKeys.pageContent(pageId || ""),
    queryFn: async (): Promise<ConfluencePageContent> => {
      const response = await axiosClient.get<ConfluencePageContent>(
        `/onboarding-documentation/page/${pageId}`
      );
      return response.data;
    },
    enabled: !!pageId && options?.enabled !== false,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    retry: 1
  });
}
